import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { buildApiUrl } from '../../config/apiConfig';
import { useNotifications } from '../../context/NotificationContext';
import '../../styles/login.css';
import './ResetPasswordPage.css';
import { logError } from '../../utils/logger';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { notify } = useNotifications();

  const [form, setForm] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  // Verificar token al cargar
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        setTokenValid(false);
        notify({
          message: 'Token no proporcionado. Por favor, usa el enlace del correo.',
          type: 'error',
        });
        return;
      }

      try {
        const response = await fetch(buildApiUrl(`/api/auth/verify-reset-token?token=${token}`));
        const data = await response.json().catch(() => ({}));

        if (response.ok && data.valid) {
          setTokenValid(true);
          setUserInfo(data.user);
        } else {
          setTokenValid(false);
          notify({
            message: data.error || 'Token inválido o expirado. Por favor, solicita uno nuevo.',
            type: 'error',
          });
        }
      } catch (error) {
        logError('[ResetPasswordPage] Error verificando token:', error);
        setTokenValid(false);
        notify({
          message: 'Error al verificar el token. Intenta nuevamente.',
          type: 'error',
        });
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token, notify]);

  const validate = useCallback(() => {
    const validationErrors = {};

    if (!form.newPassword) {
      validationErrors.newPassword = 'La nueva contraseña es obligatoria';
    } else if (form.newPassword.length < 8) {
      validationErrors.newPassword = 'La contraseña debe tener al menos 8 caracteres';
    }

    if (!form.confirmPassword) {
      validationErrors.confirmPassword = 'Confirma tu contraseña';
    } else if (form.newPassword !== form.confirmPassword) {
      validationErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    return validationErrors;
  }, [form.newPassword, form.confirmPassword]);

  const handleChange = useCallback((event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      const validationErrors = validate();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setErrors({});
      setLoading(true);

      try {
        const response = await fetch(buildApiUrl('/api/auth/reset-password'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token,
            newPassword: form.newPassword,
            confirmPassword: form.confirmPassword,
          }),
        });

        const data = await response.json().catch(() => ({}));

        if (response.ok) {
          notify({
            message: 'Contraseña actualizada exitosamente. Ya puedes iniciar sesión.',
            type: 'success',
          });
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 2000);
        } else {
          const errorText = data?.error || 'Error al actualizar la contraseña. Intenta nuevamente.';
          setErrors({ submit: errorText });
          notify({
            message: errorText,
            type: 'error',
          });
        }
      } catch (error) {
        logError('[ResetPasswordPage] Error:', error);
        setErrors({ submit: 'Error de conexión. Intenta nuevamente.' });
        notify({
          message: 'Error de conexión. Intenta nuevamente.',
          type: 'error',
        });
      } finally {
        setLoading(false);
      }
    },
    [token, form.newPassword, form.confirmPassword, navigate, notify, validate],
  );

  if (verifying) {
    return (
      <div className="login-page reset-password-page">
        <div className="login-container">
          <div className="login-form-section">
            <div className="verifying-token">
              <div className="spinner"></div>
              <p>Verificando token...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="login-page reset-password-page">
        <div className="login-container">
          <div className="login-form-section">
            <div className="reset-password-error">
              <div className="error-icon">
                <i className="fas fa-exclamation-circle"></i>
              </div>
              <h3>Token inválido o expirado</h3>
              <p>
                El enlace de recuperación no es válido o ha expirado. Por favor, solicita uno nuevo.
              </p>
              <div className="reset-password-actions">
                <Link to="/forgot-password" className="btn-request-new">
                  <i className="fas fa-redo"></i>
                  Solicitar nuevo enlace
                </Link>
                <Link to="/login" className="btn-back-login">
                  <i className="fas fa-arrow-left"></i>
                  Volver al inicio de sesión
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page reset-password-page">
      <div className="login-container">
        <div className="login-form-section">
          <div className="login-form-header">
            <h2>Restablecer Contraseña</h2>
            {userInfo && (
              <p>
                Hola <strong>{userInfo.nombre} {userInfo.apellido}</strong>, ingresa tu nueva contraseña
              </p>
            )}
          </div>

          <form className="login-form" onSubmit={handleSubmit} noValidate>
            {errors.submit ? (
              <div className="auth-message error" role="alert">
                {errors.submit}
              </div>
            ) : null}

            <div className="form-group">
              <label htmlFor="newPassword" className="form-label">
                <i className="fas fa-lock" aria-hidden="true" />
                Nueva Contraseña
              </label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={form.newPassword}
                onChange={handleChange}
                className={`form-input${errors.newPassword ? ' error' : ''}`}
                placeholder="Mínimo 8 caracteres"
                autoComplete="new-password"
                required
                autoFocus
              />
              {errors.newPassword ? <div className="form-error">{errors.newPassword}</div> : null}
              <div className="form-help">
                <i className="fas fa-info-circle"></i>
                La contraseña debe tener al menos 8 caracteres
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                <i className="fas fa-lock" aria-hidden="true" />
                Confirmar Contraseña
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                className={`form-input${errors.confirmPassword ? ' error' : ''}`}
                placeholder="Confirma tu contraseña"
                autoComplete="new-password"
                required
              />
              {errors.confirmPassword ? (
                <div className="form-error">{errors.confirmPassword}</div>
              ) : null}
            </div>

            <button type="submit" className="login-submit" disabled={loading}>
              <span
                className="auth-loading"
                aria-hidden="true"
                style={{ display: loading ? 'inline-block' : 'none' }}
              />
              <i className="fas fa-key" aria-hidden="true" />
              {loading ? 'Actualizando...' : 'Actualizar Contraseña'}
            </button>
          </form>

          <div className="register-link">
            <Link to="/login">
              <i className="fas fa-arrow-left"></i>
              Volver al inicio de sesión
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;

